/* ============================================================
   Fecha aproximada de entrega

   Se calcula aquí y no en Liquid porque Shopify cachea el HTML de la ficha:
   una fecha renderizada en el servidor se congelaría y acabaría anunciando
   un día que ya pasó. En el navegador siempre es hoy.

   Los nombres de día y mes los da Intl en el idioma de la página; las
   frases que los unen llegan del Liquid ya traducidas (locales,
   daza.entrega), con marcadores [desde], [hasta], [mes]… que se cambian
   aquí. Los días sin despacho son un ajuste del bloque (AAAA-MM-DD).
   ============================================================ */
(() => {
  const clave = (d) => `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
  const masDias = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, 12);

  /* Festivos de Colombia, calculados y no en una lista que caduca. Ley 51 de
     1983 («Emiliani»): seis fijos, Jueves y Viernes Santo, y el resto pasa al
     lunes siguiente si no cae en lunes, incluidos los tres que dependen de la
     Pascua (Ascensión, Corpus Christi y Sagrado Corazón). */
  const pascua = (a) => {
    const x = a % 19;
    const b = Math.floor(a / 100);
    const c = a % 100;
    const h = (19 * x + b - Math.floor(b / 4) - Math.floor((b - Math.floor((b + 8) / 25) + 1) / 3) + 15) % 30;
    const l = (32 + 2 * (b % 4) + 2 * Math.floor(c / 4) - h - (c % 4)) % 7;
    const m = Math.floor((x + 11 * h + 22 * l) / 451);
    const mes = Math.floor((h + l - 7 * m + 114) / 31);
    const dia = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(a, mes - 1, dia, 12);
  };
  const alLunes = (d) => masDias(d, (8 - d.getDay()) % 7);
  const cacheFestivos = {};
  const festivos = (a) => {
    if (cacheFestivos[a]) return cacheFestivos[a];
    const f = (m, d) => new Date(a, m - 1, d, 12);
    const p = pascua(a);
    const lista = [
      f(1, 1), f(5, 1), f(7, 20), f(8, 7), f(12, 8), f(12, 25),
      masDias(p, -3), masDias(p, -2),
      ...[f(1, 6), f(3, 19), f(6, 29), f(8, 15), f(10, 12), f(11, 1), f(11, 11),
        masDias(p, 39), masDias(p, 60), masDias(p, 68)].map(alLunes),
    ];
    cacheFestivos[a] = new Set(lista.map(clave));
    return cacheFestivos[a];
  };

  /* «2026-12-24» → la misma clave que usa `clave`. Lo que no tenga ese
     formato se ignora: un error de tecleo no puede romper la fecha. */
  const leerFechas = (texto) => new Set(
    (texto || '').split(/[\s,;]+/).map((t) => {
      const m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
      return m ? `${+m[1]}-${+m[2]}-${+m[3]}` : null;
    }).filter(Boolean),
  );

  /* Suma días saltando los que no son hábiles. Se cuentan sólo los días
     hábiles avanzados, así que «2 días hábiles» un viernes cae en martes, no
     en domingo —que es justo el error que hace desconfiar de estas fechas—.
     El tope evita un bucle sin fin si algún día cambian las reglas. */
  const sumarHabiles = (desde, dias, esHabil) => {
    let d = desde;
    let quedan = dias;
    for (let i = 0; quedan > 0 && i < 400; i += 1) {
      d = masDias(d, 1);
      if (esHabil(d)) quedan -= 1;
    }
    return d;
  };

  const sumarCorridos = (desde, dias) => masDias(desde, dias);

  /* Fecha y hora de Bogotá, no las del dispositivo: el despacho es en
     Colombia aunque la clienta mire la ficha desde otro huso. */
  const ahoraBogota = () => {
    const partes = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Bogota', year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', hourCycle: 'h23',
    }).formatToParts(new Date()).forEach(({ type, value }) => { partes[type] = parseInt(value, 10); });
    return { dia: new Date(partes.year, partes.month - 1, partes.day, 12), hora: partes.hour };
  };

  class DazaEntrega extends HTMLElement {
    connectedCallback() {
      try {
        this.pintar();
      } catch (e) {
        /* Si algo falla se queda el respaldo en días hábiles, que ya está en
           el HTML. Nunca se deja el hueco vacío. */
      }
    }

    pintar() {
      const salida = this.querySelector('[data-daza-fechas]');
      if (!salida) return;

      const min = parseInt(this.dataset.min, 10);
      const max = Math.max(min, parseInt(this.dataset.max, 10));
      if (!Number.isFinite(min) || !Number.isFinite(max)) return;

      const corte = parseInt(this.dataset.corte, 10);
      const habiles = this.dataset.habiles === '1';
      const conFestivos = this.dataset.festivos !== '0';
      const cerrados = leerFechas(this.dataset.sinDespacho);
      const cerrado = (d) => cerrados.has(clave(d));
      const esHabil = (d) => d.getDay() !== 0 && d.getDay() !== 6 && !cerrado(d)
        && !(conFestivos && festivos(d.getFullYear()).has(clave(d)));
      const sumar = habiles
        ? (d, n) => sumarHabiles(d, n, esHabil)
        : sumarCorridos;

      /* Primero el día de despacho: hoy si aún no pasó la hora de corte y
         (contando hábiles) hoy se despacha; si no, el siguiente. Un pedido de
         sábado sale el lunes: contar desde el sábado prometía un día de menos.
         Después de la hora de corte el pedido sale al día siguiente: prometer
         el mismo día a las 11 de la noche es prometer de más. Contando días
         corridos, el despacho solo salta los días sin despacho del ajuste. */
      const { dia: hoy, hora } = ahoraBogota();
      const pasoCorte = Number.isFinite(corte) && hora >= corte;
      let despacho = hoy;
      if (habiles) {
        if (pasoCorte || !esHabil(hoy)) despacho = sumarHabiles(hoy, 1, esHabil);
      } else {
        if (pasoCorte) despacho = sumarCorridos(hoy, 1);
        for (let i = 0; cerrado(despacho) && i < 400; i += 1) despacho = sumarCorridos(despacho, 1);
      }

      const desde = sumar(despacho, min);
      const hasta = sumar(despacho, max);
      salida.textContent = this.formatear(desde, hasta);
    }

    formatear(desde, hasta) {
      const idioma = document.documentElement.lang || undefined;
      const semana = new Intl.DateTimeFormat(idioma, { weekday: 'long' });
      const mes = new Intl.DateTimeFormat(idioma, { month: 'long' });
      const dia = (d) => `${semana.format(d)} ${d.getDate()}`;
      const poner = (plantilla, valores) => Object.keys(valores)
        .reduce((texto, k) => texto.split(`[${k}]`).join(valores[k]), plantilla);
      /* Mismo mes: no se repite. «martes 30 y el jueves 2 de octubre» se lee
         peor que «martes 30 de septiembre y jueves 2 de octubre» sólo cuando
         el mes cambia; dentro del mismo mes, repetirlo sobra. Sin plantilla
         se lanza el error y se queda el plazo en días del HTML. */
      const mismoMes = desde.getMonth() === hasta.getMonth();
      const plantilla = mismoMes ? this.dataset.mismoMes : this.dataset.distintoMes;
      if (!plantilla) throw new Error('daza-entrega: sin plantilla');
      return poner(plantilla, {
        desde: dia(desde),
        hasta: dia(hasta),
        mes: mes.format(hasta),
        mes_desde: mes.format(desde),
        mes_hasta: mes.format(hasta),
      });
    }
  }

  if (!customElements.get('daza-entrega')) {
    customElements.define('daza-entrega', DazaEntrega);
  }
})();
